/* eslint-disable consistent-return */
import { ELEMENT_PARAGRAPH } from '@doltech/utils/lib/constants';
import { getEmptyParagraphNode } from '@doltech/utils/lib/editor';
import { dynamicEditorComponent } from '@doltech/utils/lib/dynamicWithProps/dynamicEditorComponent';
import isHotkey from 'is-hotkey';
import * as React from 'react';
import { Editor, Range, Text, Transforms } from 'slate';
import { jsx } from 'slate-hyperscript';
import { ReactEditor, RenderElementProps } from 'slate-react';

import { defaultIsEmptyText } from '../../../../shared/components/IELTSample/Outline/Paragraph/components/EditorPlaceHolder';
import { SlatePlugin } from '../../../../slatePlugins/slatePlugins.types';
import {
  BulletPointPluginOptions,
  ELEMENT_BULLET_POINT,
  ELEMENT_BULLET_POINT_CHECK,
  ELEMENT_BULLET_POINT_LIST_ITEM,
  ELEMENT_BULLET_POINT_NUMBER,
  ELEMENT_BULLET_POINT_QUESTION,
  ELEMENT_BULLET_POINT_X,
} from './BulletPointPlugin.types';
import { matchBulletPoint, matchListItem } from './model/transforms';
import { withDynamicBlogEditorPluginHydration } from '@doltech/core/lib/hooks/withDolHydrationOnDemand';
import { BulletPointReadOnlyPlugin } from './BulletPointReadOnlyPlugin';

const ListItemElement = withDynamicBlogEditorPluginHydration()(
  dynamicEditorComponent(() =>
    import(/* webpackExports: "ListItemElement" */ './element/ListItemElement').then(
      (mod) => mod.ListItemElement
    )
  )
);
const OrderedListElementEditable = withDynamicBlogEditorPluginHydration()(
  dynamicEditorComponent(() =>
    import(/* webpackExports: "OrderedListElementEditable" */ './element/OrderedListElement').then(
      (mod) => mod.OrderedListElementEditable
    )
  )
);
const UnorderedListCheckElementEditable = withDynamicBlogEditorPluginHydration()(
  dynamicEditorComponent(() =>
    import(
      /* webpackExports: "UnorderedListCheckElementEditable" */ './element/UnorderedListCheckElement'
    ).then((mod) => mod.UnorderedListCheckElementEditable)
  )
);
const UnorderedListElementEditable = withDynamicBlogEditorPluginHydration()(
  dynamicEditorComponent(() =>
    import(
      /* webpackExports: "UnorderedListElementEditable" */ './element/UnorderedListElement'
    ).then((mod) => mod.UnorderedListElementEditable)
  )
);
const UnorderedListOElementEditable = withDynamicBlogEditorPluginHydration()(
  dynamicEditorComponent(() =>
    import(
      /* webpackExports: "UnorderedListOElementEditable" */ './element/UnorderedListOElement'
    ).then((mod) => mod.UnorderedListOElementEditable)
  )
);
const UnorderedListXElementEditable = withDynamicBlogEditorPluginHydration()(
  dynamicEditorComponent(() =>
    import(
      /* webpackExports: "UnorderedListXElementEditable" */ './element/UnorderedListXElement'
    ).then((mod) => mod.UnorderedListXElementEditable)
  )
);

const deserializeBulletPoint = (el) => {
  let children = Array.from(el.childNodes).map(deserializeBulletPoint);

  if (children.length === 0) {
    children = [{ text: '' }] as any;
  }

  switch (el.nodeName) {
    case 'OL': {
      return jsx('element', { type: ELEMENT_BULLET_POINT_NUMBER }, children);
    }
    case 'UL': {
      return jsx('element', { type: ELEMENT_BULLET_POINT }, children);
    }
    case 'LI': {
      return jsx('element', { type: ELEMENT_BULLET_POINT_LIST_ITEM }, children);
    }
  }

  return undefined;
};

export const BulletPointPlugin = (options: BulletPointPluginOptions = {}): SlatePlugin => {
  if (options.isReadOnly) {
    return BulletPointReadOnlyPlugin();
  }

  return {
    renderElement: (renderProps: RenderElementProps) => {
      const { element } = renderProps;
      switch (element.type) {
        case ELEMENT_BULLET_POINT: {
          return <UnorderedListElementEditable {...renderProps} {...options} />;
        }
        case ELEMENT_BULLET_POINT_CHECK: {
          return <UnorderedListCheckElementEditable {...renderProps} {...options} />;
        }
        case ELEMENT_BULLET_POINT_X: {
          return <UnorderedListXElementEditable {...renderProps} {...options} />;
        }
        case ELEMENT_BULLET_POINT_QUESTION: {
          return <UnorderedListOElementEditable {...renderProps} {...options} />;
        }
        case ELEMENT_BULLET_POINT_NUMBER: {
          return <OrderedListElementEditable {...renderProps} {...options} />;
        }
        case ELEMENT_BULLET_POINT_LIST_ITEM: {
          return <ListItemElement {...renderProps} {...options} />;
        }
      }
    },
    deserialize: {
      element: [
        {
          type: ELEMENT_BULLET_POINT,
          deserialize: deserializeBulletPoint,
        },
      ],
    },
    onKeyDown: (e, editor) => {
      const isIndentPress = isHotkey(['tab'], e);
      const isUnindentPress = isHotkey(['shift+tab'], e);

      if (!editor.selection) return;

      if (isIndentPress) {
        e.preventDefault();
        const nodeEntries = Editor.nodes(editor, {
          at: editor.selection,
          match: matchListItem,
          mode: 'lowest',
        });
        const [match] = Editor.nodes(editor, {
          at: editor.selection,
          match: matchBulletPoint,
        }) as any;
        if (match) {
          const bulletType = match[0].type;
          const arrNodes = Array.from(nodeEntries);

          // if there is paths that end with 0 then don't indent it
          const canIndent = arrNodes.every((nodeEntry) => {
            const [, path] = nodeEntry as any;
            return path[path.length - 1] !== 0;
          });

          if (!canIndent) return;

          Editor.withoutNormalizing(editor, () => {
            arrNodes.forEach((nodeEntry) => {
              const [, path] = nodeEntry;
              Transforms.wrapNodes(editor, { type: bulletType, children: [] }, { at: path });
            });
          });
        }
      } else if (isUnindentPress) {
        e.preventDefault();

        Editor.withoutNormalizing(editor, () => {
          const nodeEntries = Editor.nodes(editor, {
            at: editor.selection,
            match: matchListItem,
            mode: 'lowest',
            reverse: true,
          });
          for (const [, path] of nodeEntries) {
            Transforms.liftNodes(editor, { at: path });
          }
        });
      }
    },
  };
};
export const withBulletPointPluginEditorChain =
  () =>
  <T extends ReactEditor>(editor: T) => {
    const { deleteBackward, deleteForward, insertBreak, normalizeNode } = editor;

    editor.normalizeNode = (entry) => {
      const [node, path] = entry as [any, any[]];

      switch (node?.type) {
        // ==============================
        // 1) BULLET POINT / UL NODE
        // ==============================
        case ELEMENT_BULLET_POINT:
        case ELEMENT_BULLET_POINT_CHECK:
        case ELEMENT_BULLET_POINT_X:
        case ELEMENT_BULLET_POINT_QUESTION:
        case ELEMENT_BULLET_POINT_NUMBER: {
          // If the bullet container is totally empty, remove it
          if (node.children.length === 0) {
            Transforms.removeNodes(editor, { at: path });
            return;
          }

          // ----------------------------------------------------------------
          // NOTE: We are NOT merging with previous/next bullet nodes anymore
          // or flattening nested lists automatically. That is the difference
          // from your original code.
          // ----------------------------------------------------------------

          // All children *should* be list items. If not, remove or wrap them.
          if (
            node.children.some((liNode: any) => liNode?.type !== ELEMENT_BULLET_POINT_LIST_ITEM)
          ) {
            const incorrectNodePaths: number[] = [];
            // If there's exactly one child, and it's a paragraph, wrap it in LI:
            if (node.children.length === 1 && node.children[0].type === ELEMENT_PARAGRAPH) {
              Transforms.wrapNodes(
                editor,
                { type: ELEMENT_BULLET_POINT_LIST_ITEM, children: [] },
                { at: [...path, 0] }
              );
              return;
            }

            // Otherwise remove all that are not LI
            node.children.forEach((liNode: any, liIndex: number) => {
              if (liNode?.type !== ELEMENT_BULLET_POINT_LIST_ITEM) {
                incorrectNodePaths.push(liIndex);
              }
            });
            incorrectNodePaths.reverse().forEach((pathIndex) => {
              Transforms.removeNodes(editor, { at: [...path, pathIndex] });
            });
            return;
          }

          return;
        }

        // ==============================
        // 2) LIST ITEM NODE
        // ==============================
        case ELEMENT_BULLET_POINT_LIST_ITEM: {
          // If LI is at top-level (path.length === 1), lift its children.
          // This prevents a top-level LI from existing outside a bullet container.
          if (path.length === 1) {
            Transforms.liftNodes(editor, { at: [...path, 0] });
            return;
          }

          // If the LI has child `li` nodes, move them up
          const childrenCount = node.children.length;
          const liChildrenIndex = node.children.findIndex(matchListItem);
          if (liChildrenIndex > -1) {
            // Move any sibling nodes after it, then lift that LI
            if (liChildrenIndex < childrenCount - 1) {
              Transforms.moveNodes(editor, {
                at: [...path, liChildrenIndex + 1],
                to: [...path, liChildrenIndex, 1],
              });
            }
            Transforms.liftNodes(editor, { at: [...path, liChildrenIndex] });
            return;
          }

          // If the second child is not a bullet container, wrap it in an LI
          const secondNode = node.children[1];
          if (secondNode && secondNode.type && !matchBulletPoint(secondNode)) {
            Transforms.wrapNodes(
              editor,
              { type: ELEMENT_BULLET_POINT_LIST_ITEM, children: [] },
              { at: [...path, 1] }
            );
            return;
          }

          // If this LI has exactly one child, and that child is a bullet container,
          // unwrap. (So we don't get li > ul structure, if that’s undesired.)
          if (childrenCount === 1) {
            const firstNode = node.children[0];
            if (matchBulletPoint(firstNode)) {
              Transforms.unwrapNodes(editor, { at: path });
              return;
            }
          }

          // If this LI only has text nodes => wrap them in a paragraph
          if (node.children.every((child: any) => Text.isText(child))) {
            Transforms.wrapNodes(
              editor,
              { type: ELEMENT_PARAGRAPH, children: [] },
              {
                at: path,
                match: (n) => Text.isText(n),
              }
            );
            return;
          }

          return;
        }

        default:
          break;
      }

      // Default behavior: if not handled above, run the original normalizeNode
      normalizeNode(entry);
    };

    // =====================
    // DELETE BACKWARD
    // =====================
    editor.deleteBackward = (unit) => {
      const { selection } = editor;
      if (selection) {
        // Find the lowest list item containing the cursor
        const [match] = Editor.nodes(editor, {
          at: selection,
          match: matchListItem,
          mode: 'lowest',
        });
        if (match) {
          // If text is selected, just delete
          if (Range.isExpanded(selection)) {
            Transforms.delete(editor, { at: selection });
            return;
          }
          const [, liPath] = match;
          const liIndex = liPath[liPath.length - 1];
          const isSelectionAtStart = Editor.isStart(editor, selection.focus, [...liPath, 0]);

          // If the cursor is at the very start of the first LI => lift
          if (isSelectionAtStart && liIndex === 0) {
            Transforms.liftNodes(editor, { at: liPath });
            return;
          }
        }
      }
      deleteBackward(unit);
    };

    // =====================
    // DELETE FORWARD
    // =====================
    editor.deleteForward = (unit) => {
      // Only skip default if we’re inside a bullet container (to avoid merging)
      if (editor.selection) {
        const [match] = Editor.nodes(editor, {
          at: editor.selection,
          match: matchBulletPoint,
        });
        if (match) {
          // Do nothing special => effectively ignore forward delete
          return;
        }
      }
      deleteForward(unit);
    };

    // =====================
    // INSERT BREAK
    // (Pressing Enter)
    // =====================
    editor.insertBreak = () => {
      const { selection } = editor;

      if (selection) {
        const [match] = Editor.nodes(editor, {
          at: selection,
          match: matchListItem,
          mode: 'lowest',
        });
        if (match) {
          if (Range.isExpanded(selection)) {
            // If there is a selection, delete first
            Transforms.delete(editor, { at: selection });
            return;
          }

          const [liNode, liPath] = match;
          const liIndex = liPath[liPath.length - 1];
          const liParagraph = liNode.children[0]; // usually the paragraph
          const isLiEmpty = defaultIsEmptyText(liParagraph);

          // The parent bullet container
          const [ulNode] = Editor.parent(editor, liPath);
          const isLastLi = ulNode.children.length - 1 === liIndex;

          // If this is the last LI and it's empty => lift
          if (isLastLi && isLiEmpty) {
            // Check if we’re actually inside a bullet container or something else
            const [node] = Editor.node(editor, [liPath[0]]);
            if (!matchBulletPoint(node)) {
              // If not inside a bullet container, just convert LI => paragraph
              Editor.withoutNormalizing(editor, () => {
                try {
                  Transforms.setNodes(editor, { type: ELEMENT_PARAGRAPH }, { at: liPath });
                  // Lift up all the levels until we’re out (if that’s your style)
                  for (let i = 1; i < liPath.length - 1; i++) {
                    Transforms.liftNodes(editor, { at: liPath });
                  }
                } catch (ex) {
                  // swallow error
                }
              });
              return;
            }
            // Otherwise lift the empty LI from the bullet container
            Transforms.liftNodes(editor, { at: liPath });
            return;
          }

          const isSelectionAtStart = Editor.isStart(editor, editor.selection.focus, [...liPath, 0]);
          const isSelectionAtEnd = Editor.isEnd(editor, editor.selection.focus, [...liPath, 0]);
          const isSelectionAtMiddle = !isSelectionAtStart && !isSelectionAtEnd;

          // If at start of an empty LI => lift
          if (isSelectionAtStart && isLiEmpty) {
            Transforms.liftNodes(editor, { at: liPath });
            return;
          }

          // If at start (but not empty) => insert new LI above
          if (isSelectionAtStart) {
            Transforms.insertNodes(
              editor,
              {
                type: ELEMENT_BULLET_POINT_LIST_ITEM,
                children: [getEmptyParagraphNode()],
              },
              { at: liPath }
            );
            return;
          }

          // If at end => insert empty paragraph as a sibling inside LI
          if (isSelectionAtEnd) {
            Transforms.insertNodes(editor, getEmptyParagraphNode(), {
              at: [...liPath, liNode.children.length],
              select: true,
            });
            return;
          }

          // If in the middle => split
          if (isSelectionAtMiddle) {
            // You might want to ensure we only split the paragraph or the LI
            Transforms.splitNodes(editor, {
              match: (n) => n === liNode,
            });
            return;
          }

          return;
        }
      }

      // Default behavior
      insertBreak();
    };

    return editor;
  };
